import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  GlassFeedContainer,
  LoadingContainer,
  EmptyStateContainer,
} from "../layouts/GlassTileLayout";

import {
  GlassImageContainer,
  GlassModal,
  GlassModalContent,
  PhotoCarouselImage,
  ModalImage,
  NavigationControls,
  ModalNavigationControls,
  PhotoCounter,
} from "../layouts/PhotoCarouselGlassComponents"; // New specialized components
import { usePhotoCarousel } from "../../hooks/usePhotoCarousel"; // New hook

function PhotoCarousel({
  size = "small",
  numImages = 10,
  autoPlay = false,
  autoPlayInterval = 5000,
  showArrows = true,
}) {
  console.log("PhotoCarousel: Component rendering/re-rendering. Props:", {
    size,
    numImages,
    autoPlay,
    autoPlayInterval,
    showArrows,
  });

  const {
    // State
    currentIndex,
    imageUrls,
    loading,
    error,
    modalOpen,
    currentLargeImageUrl,

    // Actions
    goToPrevious,
    goToNext,
    handleImageClick,
    handleModalClose,
    handleModalPrevious,
    handleModalNext,

    // Computed
    hasImages,
    canNavigate,
    currentImageUrl,
  } = usePhotoCarousel({
    size,
    numImages,
    autoPlay,
    autoPlayInterval,
  });

  // Debug logging for state changes
  React.useEffect(() => {
    console.log("PhotoCarousel: currentIndex changed to:", currentIndex);
  }, [currentIndex]);

  React.useEffect(() => {
    console.log("PhotoCarousel: imageUrls changed. Count:", imageUrls.length);
  }, [imageUrls]);

  React.useEffect(() => {
    console.log("PhotoCarousel: loading state changed to:", loading);
  }, [loading]);

  React.useEffect(() => {
    if (error) {
      console.error("PhotoCarousel: error state changed:", error);
    }
  }, [error]);

  React.useEffect(() => {
    console.log("PhotoCarousel: modalOpen state changed to:", modalOpen);
  }, [modalOpen]);

  React.useEffect(() => {
    console.log(
      "PhotoCarousel: currentLargeImageUrl changed to:",
      currentLargeImageUrl
    );
  }, [currentLargeImageUrl]);

  // Loading state
  if (loading) {
    console.log("PhotoCarousel: Rendering loading state.");
    return (
      <GlassFeedContainer>
        <LoadingContainer>
          <CircularProgress size={48} thickness={4} />
        </LoadingContainer>
      </GlassFeedContainer>
    );
  }

  // Error state
  if (error) {
    console.log("PhotoCarousel: Rendering error state.");
    return (
      <GlassFeedContainer>
        <EmptyStateContainer>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading images
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {error}
          </Typography>
        </EmptyStateContainer>
      </GlassFeedContainer>
    );
  }

  console.log(
    "PhotoCarousel: Rendering main content. Current index:",
    currentIndex,
    "Image URL:",
    currentImageUrl
  );

  return (
    <GlassFeedContainer>
      {/* Main carousel content - now vertical layout */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        flexGrow={1}
        gap={2}
        sx={{ minHeight: 0, p: 2 }}
      >
        {/* Image container */}
        {hasImages ? (
          <GlassImageContainer onClick={handleImageClick}>
            <PhotoCarouselImage
              src={currentImageUrl}
              alt={`Image ${currentIndex + 1}`}
              currentIndex={currentIndex}
              onClick={handleImageClick}
            />
          </GlassImageContainer>
        ) : (
          <EmptyStateContainer>
            <Typography variant="body1">No images available</Typography>
          </EmptyStateContainer>
        )}

        {/* Navigation controls - now below image, side by side */}
        {showArrows && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={3}
            width="100%"
          >
            <NavigationControls
              showArrows={showArrows}
              onPrevious={goToPrevious}
              onNext={goToNext}
              disabled={loading}
              canNavigate={canNavigate}
            />
          </Box>
        )}
      </Box>

      {/* Photo counter */}
      {hasImages && (
        <PhotoCounter current={currentIndex} total={imageUrls.length} />
      )}

      {/* Glassmorphic Modal */}
      <GlassModal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="image-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <GlassModalContent>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              minHeight: "60vh",
            }}
          >
            <ModalNavigationControls
              onPrevious={handleModalPrevious}
              onNext={handleModalNext}
              onClose={handleModalClose}
            />

            {currentLargeImageUrl && (
              <ModalImage
                src={currentLargeImageUrl}
                alt="Large Image"
                onError={(e) => {
                  console.error(
                    `PhotoCarousel: Error loading large image source: ${currentLargeImageUrl}`
                  );
                  e.target.src = "/missing.jpg";
                }}
              />
            )}

            {hasImages && (
              <PhotoCounter
                current={currentIndex}
                total={imageUrls.length}
                showInModal={true}
              />
            )}
          </Box>
        </GlassModalContent>
      </GlassModal>
    </GlassFeedContainer>
  );
}

export default PhotoCarousel;
